// @ts-strict-ignore
import {
    allLifecycleEventsForPlatform,
    automationTypeMatchesLifecycleStep,
    findLifecycleAutomationRun,
    getLifecycleStepDisplayText,
} from '@/components/automations/automationHelperComponents';
import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { LifecycleStep, AutomationRun, ReleaseStatus, ReleaseTimelinePhase } from '@/types';
import Divider from 'antd/lib/divider';
import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '@/timeHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import { Destination } from '@/types/destinations';
import { Nullable } from '@/typescriptHelpers';
import { MiniDateBlockBase } from '@/components/appHome/widgets/TargetDatesWidget';
import { capitalize, uniq } from 'lodash';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { CalendarOutlined, CheckCircleFilled } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import Paragraph from 'antd/lib/typography/Paragraph';
import Flex from 'antd/lib/flex';

export interface IWorkflowRunDatesWidgetProps {
    selectedDestinations: Destination[];
    workflowRunStatus: ReleaseStatus;
    workflowRunTimelinePhase: ReleaseTimelinePhase;
    workflowsEnabledLifecycleSteps: LifecycleStep[];
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    skippedAt?: string;
    kickedOffAt?: string;
    submittedAtByDestinationStepId?: Record<string, string>;
    releasedAtByDestinationStepId?: Record<string, string>;
    legacyLifecycleAutoRuns?: AutomationRun[];
    workflowAutoRunsByDestination?: Record<string, AutomationRun[]>;
    appId: string;
}

export const WorkflowRunDatesWidgetProps: React.FC<IWorkflowRunDatesWidgetProps> = ({
    selectedDestinations,
    workflowRunStatus,
    workflowsEnabledLifecycleSteps,
    targetKickoffDate,
    targetSubmitDate,
    targetReleaseDate,
    skippedAt,
    kickedOffAt,
    submittedAtByDestinationStepId,
    releasedAtByDestinationStepId,
    legacyLifecycleAutoRuns,
    workflowAutoRunsByDestination,
    appId,
}) => {
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const timeFormat = (targetDate: dayjs.Dayjs) => (targetDate.minute() !== 0 ? 'h:mm A' : DatetimeFormat.Hour);
    const dateFormat = DatetimeFormat.ShortDayMonthDate;

    const tzDate = (date: string) => (effectiveTimezone ? dayjs(date).tz(effectiveTimezone) : dayjs(date));

    const isSkipped = workflowRunStatus === ReleaseStatus.Skipped;

    let skippedAtDate = null;
    let skippedAtTime = null;
    if (isSkipped) {
        skippedAtDate = skippedAt ? tzDate(skippedAt).format(dateFormat) : null;
        skippedAtTime = skippedAt ? tzDate(skippedAt).format(timeFormat(tzDate(skippedAt))) : null;
    }

    const lifecycleStepsData =
        isSkipped ? null : (
            getBackwardsCompatibleDateInfosForLifecycleSteps(
                selectedDestinations,
                workflowsEnabledLifecycleSteps,
                targetKickoffDate,
                targetSubmitDate,
                targetReleaseDate,
                kickedOffAt,
                submittedAtByDestinationStepId,
                releasedAtByDestinationStepId,
                legacyLifecycleAutoRuns,
                workflowAutoRunsByDestination,
            )
        );

    return (
        <Flex
            vertical
            align="stretch"
            flex={1}
            style={{
                borderColor: antGrey.grey4,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 8,
                backgroundColor: antGrey.grey3,
                padding: '12px 20px',
                marginBottom: 26,
            }}
        >
            {isSkipped && (
                <MiniDateBlockBase
                    displayDate={skippedAtDate}
                    displayTime={skippedAtTime}
                    displayTimeMoment={skippedAt ? tzDate(skippedAt) : undefined}
                    effectiveTimezone={effectiveTimezone}
                    use24HourTimeFormat={use24HourTimeFormat}
                    title={'Skipped'}
                    automationIcon={false}
                    isComplete={false}
                    hasDate
                    shouldBeBolded={false}
                    showCadenceIcon={false}
                />
            )}
            {!isSkipped && (
                <>
                    {lifecycleStepsData.map((stepDateDatum, index) => {
                        return (
                            <Flex key={index} vertical flex={1}>
                                {index ?
                                    <Divider style={{ width: '100%', margin: '10px 0' }} />
                                :   null}
                                <WorkflowRunMiniDateBlock
                                    selectedDestinations={selectedDestinations}
                                    appId={appId}
                                    {...stepDateDatum}
                                />
                            </Flex>
                        );
                    })}
                </>
            )}
        </Flex>
    );
};

interface LifecycleStepDatesDatum {
    step: LifecycleStep;
    isMigratedStep: boolean;
    targetDate?: string;
    legacyActualDate?: string;
    actualDatesByDestination?: Record<string, string>;
    legacyStepAutomation?: AutomationRun;
    stepAutomationsByDestination?: Record<string, AutomationRun[]>;
}

interface WorkflowRunMiniDateBlockProps extends LifecycleStepDatesDatum {
    selectedDestinations: Destination[];
    appId: string;
}

const WorkflowRunMiniDateBlock: React.FC<WorkflowRunMiniDateBlockProps> = ({
    step,
    selectedDestinations,
    isMigratedStep,
    targetDate,
    legacyActualDate,
    actualDatesByDestination,
    appId,
}) => {
    const migratedDateInfo =
        isMigratedStep ?
            makeMigratedLifecycleDateDisplayInfo(step, selectedDestinations, targetDate, actualDatesByDestination)
        :   null;
    const legacyDateInfo =
        !isMigratedStep ? makeLegacyLifecycleDateDisplayInfo(step, targetDate, legacyActualDate) : null;

    const title = capitalize(step);

    return (
        <div>
            <Flex
                align="center"
                gap={10}
                style={{
                    paddingBottom: '6px',
                }}
            >
                <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{title}</Text>
            </Flex>
            {isMigratedStep && (
                <>
                    {!migratedDateInfo.haveAnyActualDates && migratedDateInfo.haveTargetDate && (
                        <LifecycleDateRow
                            step={step}
                            date={migratedDateInfo.targetDate}
                            haveValidDate
                            showShortDate
                            renderDestinations
                            destinations={selectedDestinations}
                            isComplete={false}
                            isTarget={true}
                            appId={appId}
                        />
                    )}
                    {!migratedDateInfo.haveAnyActualDates && !migratedDateInfo.haveTargetDate && (
                        <LifecycleDateRow
                            step={step}
                            date={null}
                            haveValidDate={false}
                            showShortDate
                            renderDestinations={false}
                            appId={appId}
                        />
                    )}
                    {migratedDateInfo.haveAnyActualDates && (
                        <Flex vertical flex={1} gap={8}>
                            {migratedDateInfo.destinationLifecycleDateInfo.map((destinationDateInfo, index) => {
                                const destination = selectedDestinations.find(
                                    (dest) => dest.stepConfigId === destinationDateInfo.destinationStepId,
                                );
                                const foundMatchingDestination = !!destination;

                                return destinationDateInfo.haveActualDate ?
                                        <div key={index}>
                                            <LifecycleDateRow
                                                step={step}
                                                date={destinationDateInfo.actualStepDate}
                                                haveValidDate={destinationDateInfo.haveActualDate}
                                                showShortDate
                                                renderDestinations={foundMatchingDestination}
                                                destinations={foundMatchingDestination && [destination]}
                                                isComplete={true}
                                                appId={appId}
                                            />
                                        </div>
                                    :   null;
                            })}
                        </Flex>
                    )}
                </>
            )}
            {!isMigratedStep && (
                <LifecycleDateRow
                    step={step}
                    date={legacyDateInfo.displayDate}
                    haveValidDate={legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate}
                    showShortDate
                    renderDestinations={legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate}
                    destinations={
                        (legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate) && selectedDestinations
                    }
                    isComplete={legacyDateInfo.haveActualDate}
                    isTarget={!legacyDateInfo.haveActualDate && legacyDateInfo.haveTargetDate}
                    appId={appId}
                />
            )}
        </div>
    );
};

interface LifecycleDateRowProps {
    step: LifecycleStep;
    date: string;
    haveValidDate: boolean;
    renderDestinations: boolean;
    showShortDate: boolean;
    destinations?: Destination[];
    isComplete?: boolean;
    isTarget?: boolean;
    appId?: string;
}

export const LifecycleDateRow: React.FC<LifecycleDateRowProps> = ({
    step,
    date,
    haveValidDate,
    renderDestinations,
    showShortDate,
    destinations,
    isComplete,
    isTarget,
    appId,
}) => {
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const maybeDate = effectiveTimezone ? dayjs(date).tz(effectiveTimezone) : dayjs(date);
    const maybeDateDay =
        haveValidDate &&
        maybeDate.format(showShortDate ? DatetimeFormat.ShortDayMonthDateNonOrdinal : DatetimeFormat.DayMonthDate);
    const maybeDateTime = haveValidDate && maybeDate.format(maybeDate.minute() !== 0 ? 'h:mm A' : DatetimeFormat.Hour);

    const emptyDate = showShortDate ? '--' : '-- / -- / --';
    const emptyTime = '-- : --';

    const leftDisplay = haveValidDate ? maybeDateDay : emptyDate;
    const rightDisplay = haveValidDate ? maybeDateTime : emptyTime;

    return (
        <Flex
            gap={10}
            align="center"
            flex={1}
            style={{
                fontSize: '16px',
                margin: 0,
                color: !haveValidDate ? antGrey.grey6 : antGrey.grey8,
            }}
        >
            {renderDestinations && destinations?.length > 0 && (
                <div style={{ flex: 0 }}>
                    <DestinationCounter destinations={destinations} size={20} />
                </div>
            )}
            <Paragraph ellipsis style={{ color: 'inherit', fontSize: '16px', margin: 0 }}>
                <Text
                    style={{
                        color: 'inherit',
                        fontWeight: 400,
                        fontSize: '16px',
                    }}
                >
                    {leftDisplay}
                </Text>
                <Text
                    style={{
                        fontSize: '16px',
                        color: !haveValidDate ? antGrey.grey5 : antGrey.grey6,
                    }}
                >
                    {'  /  '}
                </Text>
                <Text style={{ color: 'inherit', fontSize: '16px', fontWeight: 400 }}>
                    {haveValidDate && effectiveTimezone ?
                        <TimeWithConditionalTz
                            triggeredAtLabel={rightDisplay}
                            triggeredByMoment={maybeDate}
                            use24HourTimeFormat={use24HourTimeFormat}
                            effectiveTimezone={effectiveTimezone}
                            style={{ fontWeight: 400, color: 'inherit', fontSize: '16px' }}
                        />
                    :   rightDisplay}
                </Text>
            </Paragraph>
            {!!isComplete && (
                <Text style={{ fontSize: '16px' }}>
                    <Tooltip title={`${capitalize(step)} completed on ${maybeDateDay} at ${maybeDateTime}.`}>
                        <CheckCircleFilled
                            style={{
                                color: runwayColors.statusGreen,
                            }}
                        />
                    </Tooltip>
                </Text>
            )}
            {!!isTarget && (
                <Text style={{ fontSize: '16px' }}>
                    <Tooltip title={`Target ${step} date is ${maybeDateDay} at ${maybeDateTime}.`}>
                        <CalendarOutlined
                            style={{
                                color: runwayColors.primary,
                            }}
                        />
                    </Tooltip>
                </Text>
            )}
        </Flex>
    );
};

const getBackwardsCompatibleDateInfosForLifecycleSteps = (
    selectedDestinations: Destination[],
    workflowsEnabledLifecycleSteps: LifecycleStep[],
    targetKickoffDate?: string,
    targetSubmitDate?: string,
    targetReleaseDate?: string,
    kickedOffAt?: string,
    submittedAtByDestinationStepId?: Record<string, string>,
    releasedAtByDestinationStepId?: Record<string, string>,
    legacyLifecycleAutoRuns?: AutomationRun[],
    workflowAutoRunsByDestination?: Record<string, AutomationRun[]>,
): LifecycleStepDatesDatum[] => {
    const selectedDestinationStepIds = selectedDestinations.map((destination) => destination.stepConfigId);
    const allLifecycleStepsForDestinations: LifecycleStep[] = selectedDestinations
        .reduce((combinedLifecycleSteps: LifecycleStep[], destination: Destination) => {
            const platformLifecycleSteps = allLifecycleEventsForPlatform(destination.platform);
            return uniq([...combinedLifecycleSteps, ...platformLifecycleSteps]);
        }, [])
        .sort(lifecycleStepsSortFunc);

    return allLifecycleStepsForDestinations.map((lifecycleStep) => {
        const isWorkflowsEnabledStep = workflowsEnabledLifecycleSteps.includes(lifecycleStep);

        let targetStepDate: Nullable<string>;
        switch (lifecycleStep) {
            case LifecycleStep.Kickoff:
                targetStepDate = targetKickoffDate;
                break;
            case LifecycleStep.Submission:
                targetStepDate = targetSubmitDate;
                break;
            case LifecycleStep.Release:
                targetStepDate = targetReleaseDate;
                break;
            default:
                return null;
        }

        let legacyActualStepDate: Nullable<string>;
        let actualStepDatesByDestination: Record<string, string>;

        if (isWorkflowsEnabledStep) {
            if (lifecycleStep === LifecycleStep.Submission) {
                actualStepDatesByDestination = submittedAtByDestinationStepId;
            } else if (lifecycleStep === LifecycleStep.Release) {
                actualStepDatesByDestination = releasedAtByDestinationStepId;
            }
        } else {
            if (lifecycleStep === LifecycleStep.Kickoff) {
                legacyActualStepDate = kickedOffAt;
            }
        }

        let legacyStepAutoRun: AutomationRun;
        const stepAutoRunsByDestination: Record<string, AutomationRun[]> = {};

        if (isWorkflowsEnabledStep) {
            for (const destStepId in workflowAutoRunsByDestination) {
                if (!selectedDestinationStepIds.includes(destStepId)) {
                    continue;
                }

                const destinationAutoRuns = workflowAutoRunsByDestination[destStepId];
                if (destinationAutoRuns) {
                    stepAutoRunsByDestination[destStepId] = destinationAutoRuns.filter((autoRun) => {
                        return automationTypeMatchesLifecycleStep(lifecycleStep, autoRun.type);
                    });
                }
            }
        } else {
            legacyStepAutoRun = findLifecycleAutomationRun(lifecycleStep, legacyLifecycleAutoRuns);
        }

        const lifecycleStepDatum: LifecycleStepDatesDatum = {
            step: lifecycleStep,
            isMigratedStep: isWorkflowsEnabledStep,
            targetDate: targetStepDate,
            legacyActualDate: legacyActualStepDate,
            actualDatesByDestination: isWorkflowsEnabledStep ? actualStepDatesByDestination : null,
            legacyStepAutomation: legacyStepAutoRun,
            stepAutomationsByDestination: isWorkflowsEnabledStep ? stepAutoRunsByDestination : null,
        };

        return lifecycleStepDatum;
    });
};

const makeLegacyLifecycleDateDisplayInfo = (step: LifecycleStep, targetDate?: string, actualDate?: string) => {
    const targetStepDate = dayjs(targetDate).isValid() ? dayjs(targetDate) : null;
    const actualStepDate = dayjs(actualDate).isValid() ? dayjs(actualDate) : null;

    const haveTargetDate = !!targetStepDate && !!targetDate;
    const haveActualDate = !!actualStepDate && !!actualDate;

    const dateDisplayTitle = getLifecycleStepDisplayText(step, haveActualDate, false);

    const displayDate =
        haveActualDate ? actualDate
        : haveTargetDate ? targetDate
        : null;

    return {
        haveTargetDate,
        haveActualDate,
        dateDisplayTitle,
        displayDate,
    };
};

const makeMigratedLifecycleDateDisplayInfo = (
    step: LifecycleStep,
    selectedDestinations: Destination[],
    targetDate?: string,
    actualDatesByDestination?: Record<string, string>,
) => {
    const haveTargetDateString = !!targetDate;
    const targetDateStringIsValid = dayjs(targetDate).isValid();

    let haveAnyActualDates = false;

    const destinationLifecycleDatesResult = selectedDestinations.map((dest) => {
        const actualStepDate =
            !!actualDatesByDestination && !!actualDatesByDestination[dest.stepConfigId] ?
                actualDatesByDestination[dest.stepConfigId]
            :   null;
        const haveActualDate = !!actualStepDate;

        if (haveActualDate) {
            haveAnyActualDates = true;
        }

        return {
            destinationStepId: dest.stepConfigId,
            actualStepDate: actualStepDate,
            haveActualDate: haveActualDate,
        };
    });

    return {
        haveTargetDate: haveTargetDateString,
        haveAnyActualDates: haveAnyActualDates,
        destinationLifecycleDateInfo: destinationLifecycleDatesResult,
        targetDate: haveTargetDateString && targetDateStringIsValid ? targetDate : null,
    };
};

const lifecycleStepsSortFunc = (a: LifecycleStep, b: LifecycleStep): number => {
    const stepEnumKeys = Object.keys(LifecycleStep);

    const indexOfA = stepEnumKeys.indexOf(a);
    const indexOfB = stepEnumKeys.indexOf(b);

    return indexOfA - indexOfB;
};
